/**
 * Every TanStack Query key in the admin, in one place.
 *
 * Keys are built as functions returning `as const` tuples so that invalidating a
 * prefix (`queryKeys.articles.all`) reliably catches every list and detail below
 * it. Hand-written string arrays scattered across features drift, and a drifted
 * key means a save that does not refresh the list it came from.
 */

export type ListParams = Record<string, unknown>;

export const queryKeys = {
  session: {
    current: () => ['session', 'current'] as const,
  },

  locales: {
    all: () => ['locales'] as const,
    list: () => ['locales', 'list'] as const,
  },

  options: {
    all: () => ['options'] as const,
  },

  users: {
    all: () => ['users'] as const,
    list: (params: ListParams) => ['users', 'list', params] as const,
    detail: (id: number) => ['users', 'detail', id] as const,
  },

  roles: {
    all: () => ['roles'] as const,
    list: () => ['roles', 'list'] as const,
    detail: (id: number) => ['roles', 'detail', id] as const,
  },

  permissions: {
    all: () => ['permissions'] as const,
    list: () => ['permissions', 'list'] as const,
  },

  articles: {
    all: () => ['articles'] as const,
    list: (params: ListParams) => ['articles', 'list', params] as const,
    detail: (id: number) => ['articles', 'detail', id] as const,
    revisions: (id: number) => ['articles', 'detail', id, 'revisions'] as const,
  },

  pages: {
    all: () => ['pages'] as const,
    list: (params: ListParams) => ['pages', 'list', params] as const,
    detail: (id: number) => ['pages', 'detail', id] as const,
  },

  categories: {
    all: () => ['categories'] as const,
    list: (params: ListParams) => ['categories', 'list', params] as const,
    detail: (id: number) => ['categories', 'detail', id] as const,
  },

  tags: {
    all: () => ['tags'] as const,
    list: (params: ListParams) => ['tags', 'list', params] as const,
  },

  authors: {
    all: () => ['authors'] as const,
    list: (params: ListParams) => ['authors', 'list', params] as const,
    detail: (id: number) => ['authors', 'detail', id] as const,
  },

  media: {
    all: () => ['media'] as const,
    list: (params: ListParams) => ['media', 'list', params] as const,
    detail: (id: number) => ['media', 'detail', id] as const,
  },

  seo: {
    all: () => ['seo'] as const,
    health: () => ['seo', 'health'] as const,
    keywords: (params: ListParams = {}) => ['seo', 'keywords', params] as const,
    aiQueries: (params: ListParams = {}) => ['seo', 'ai-queries', params] as const,
    metadata: (type: string, id: number) => ['seo', 'metadata', type, id] as const,
  },

  redirects: {
    all: () => ['redirects'] as const,
    list: (params: ListParams) => ['redirects', 'list', params] as const,
  },

  menus: {
    all: () => ['menus'] as const,
    list: () => ['menus', 'list'] as const,
    detail: (id: number) => ['menus', 'detail', id] as const,
  },

  settings: {
    all: () => ['settings'] as const,
    group: (group: string) => ['settings', 'group', group] as const,
  },

  gates: {
    all: () => ['gates'] as const,
    board: (params: ListParams) => ['gates', 'board', params] as const,
  },

  audit: {
    all: () => ['audit'] as const,
    list: (params: ListParams) => ['audit', 'list', params] as const,
  },

  import: {
    all: () => ['import'] as const,
    list: () => ['import', 'list'] as const,
    detail: (id: number) => ['import', 'detail', id] as const,
  },
} as const;
